import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// We nudge the user WARN_BEFORE_SEC before the session dies. 60s matches the
// 'half-written timesheet entry' use case called out in the PRD.
const WARN_BEFORE_SEC = 60;

export default function SessionTimeoutBanner() {
  const { user, expiresAt, refreshSession, logout } = useAuth();
  const [now, setNow] = useState(() => Date.now());
  const [extending, setExtending] = useState(false);

  // Tick once a second only while the banner is relevant. The effect returns
  // a clean no-op interval when the session is far from expiring so we don't
  // waste a second-level interval across the whole app lifetime.
  const expiryMs = useMemo(
    () => (expiresAt ? new Date(expiresAt).getTime() : null),
    [expiresAt]
  );
  const warnAtMs = expiryMs ? expiryMs - WARN_BEFORE_SEC * 1000 : null;
  const shouldTick = Boolean(user && expiryMs);

  useEffect(() => {
    if (!shouldTick) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [shouldTick, expiryMs]);

  useEffect(() => {
    // Hard logout exactly at expiry so a stale UI cannot keep showing cached
    // data after the cookie is gone.
    if (!expiryMs || !user) return;
    const ms = expiryMs - Date.now();
    if (ms <= 0) {
      logout();
      toast.error("Your session has expired. Please sign in again.");
      return;
    }
    const t = setTimeout(() => {
      logout();
      toast.error("Your session has expired. Please sign in again.");
    }, ms + 100);
    return () => clearTimeout(t);
  }, [expiryMs, user, logout]);

  if (!user || !expiryMs || !warnAtMs) return null;
  if (now < warnAtMs) return null;              // too early
  if (now >= expiryMs) return null;             // already expired, logout handler fires

  const remainingSec = Math.max(0, Math.ceil((expiryMs - now) / 1000));
  const mm = String(Math.floor(remainingSec / 60)).padStart(2, "0");
  const ss = String(remainingSec % 60).padStart(2, "0");

  const extend = async () => {
    setExtending(true);
    const res = await refreshSession();
    setExtending(false);
    if (res.ok) toast.success("Session extended — you have 12 more hours");
    else toast.error(res.error || "Could not extend session. Please sign in again.");
  };

  return (
    <div
      role="alert"
      data-testid="session-timeout-banner"
      className="fixed top-0 inset-x-0 z-[60] bg-amber-500 text-amber-950 shadow-md"
    >
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <div className="flex items-center gap-2 text-sm">
          <span className="inline-block w-2 h-2 rounded-full bg-amber-900 animate-pulse" />
          <span className="font-semibold">Your session expires in</span>
          <span className="font-mono font-bold text-base tabular-nums" data-testid="session-countdown">{mm}:{ss}</span>
          <span className="hidden sm:inline">— save your work or extend to keep going.</span>
        </div>
        <div className="flex items-center gap-2 sm:ml-auto">
          <button
            onClick={extend}
            disabled={extending}
            data-testid="session-extend"
            className="bg-amber-900 hover:bg-amber-800 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50"
          >
            {extending ? "Extending…" : "Keep me signed in"}
          </button>
          <button
            onClick={logout}
            data-testid="session-signout"
            className="text-xs underline underline-offset-2 hover:text-amber-900"
          >
            Sign out now
          </button>
        </div>
      </div>
    </div>
  );
}
